import { useEffect, useState } from 'react';
import { ResponsiveDialog, Button, Input, Label, Alert } from '../../components/ui';
import { api } from '../../api/client';
import type { MonthlyStatements } from '../../api/types';
import { saveBlob } from '../../lib/saveBlob';
import { zipStore } from '../../lib/zipStore';
import { defaultBillRunPeriod } from '../../lib/billRunPeriod';

// Download Monthly (Beau, 2026-10-08): the realm's finalized statements for
// one calendar month, the latest per pocket, in one zip. Statements whose PDF
// is missing are generated first through the same route as Regenerate, then
// everything is fetched one by one and zipped here, in the browser: the repo
// keeps bulk work as single calls rather than a bulk endpoint.

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthLabel = (month: string) => `${MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? ''} ${month.slice(0, 4)}`;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type Statement = MonthlyStatements['statements'][number];

function missingText(missing: Statement[], month: string): string {
  return [
    `These statements for ${monthLabel(month)} had no PDF when this zip was made:`,
    ...missing.map((s) => `- ${s.pocket_name} (${s.filename})`),
    '',
    'Open each bill run and use Regenerate, then download the month again.',
    '',
  ].join('\n');
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Injected for tests; the default month is the one before `now`. */
  now?: Date;
  pollIntervalMs?: number;
  timeoutMs?: number;
}

export default function DownloadMonthlyDialog({ open, onOpenChange, now, pollIntervalMs = 3000, timeoutMs = 120_000 }: Props) {
  const [month, setMonth] = useState(() => defaultBillRunPeriod(now ?? new Date()).start.slice(0, 7));
  const [listing, setListing] = useState<MonthlyStatements | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !/^\d{4}-\d{2}$/.test(month)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getMonthlyStatements(month)
      .then((res) => { if (!cancelled) setListing(res.data); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Could not list the statements'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, month]);

  const statements = listing?.month === month ? listing.statements : [];
  const missing = statements.filter((s) => !s.has_pdf);
  const busy = progress !== null;

  async function download() {
    if (!listing) return;
    setError(null);
    try {
      let current = listing;
      const toGenerate = current.statements.filter((s) => !s.has_pdf);
      if (toGenerate.length > 0) {
        for (const [i, s] of toGenerate.entries()) {
          setProgress(`Generating missing PDFs (${i + 1} of ${toGenerate.length})`);
          await api.generateBillRunPdfs(s.run_id);
        }
        // generate-pdfs clears pdf_url before queueing, so has_pdf turning
        // true means this generation finished, not an older file.
        const waiting = new Set(toGenerate.map((s) => s.run_id));
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
          await sleep(pollIntervalMs);
          current = (await api.getMonthlyStatements(month)).data;
          const left = current.statements.filter((s) => waiting.has(s.run_id) && !s.has_pdf).length;
          if (left === 0) break;
          setProgress(`Waiting for ${left} PDF${left === 1 ? '' : 's'} to be generated`);
        }
      }

      const files: Array<{ name: string; data: Uint8Array }> = [];
      const notIncluded: Statement[] = current.statements.filter((s) => !s.has_pdf || !s.download_url);
      const ready = current.statements.filter((s) => s.has_pdf && s.download_url);
      for (const [i, s] of ready.entries()) {
        setProgress(`Downloading ${i + 1} of ${ready.length}`);
        const res = await fetch(s.download_url as string);
        if (!res.ok) {
          notIncluded.push(s);
          continue;
        }
        files.push({ name: s.filename, data: new Uint8Array(await res.arrayBuffer()) });
      }
      if (notIncluded.length > 0) {
        files.push({ name: 'MISSING.txt', data: new TextEncoder().encode(missingText(notIncluded, month)) });
      }
      saveBlob(new Blob([zipStore(files)], { type: 'application/zip' }), current.zip_name);
      setListing(current);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setProgress(null);
    }
  }

  const n = statements.length;
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Download Monthly"
      description="Every finalized statement for one month in this realm, the latest per pocket, in one zip."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={download} disabled={loading || busy || n === 0}>Download</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="download-monthly-month">Month</Label>
          <Input
            id="download-monthly-month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            disabled={busy}
          />
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        {!error && !loading && listing?.month === month && (
          n === 0 ? (
            <p className="text-sm text-content-muted">No finalized statements for {monthLabel(month)}</p>
          ) : (
            <div className="space-y-1 text-sm">
              <p className="text-content">{n} statement{n === 1 ? '' : 's'} for {monthLabel(month)}</p>
              {missing.length > 0 && (
                <p className="text-content-muted">
                  {missing.length} without a PDF yet: {missing.map((s) => s.pocket_name).join(', ')}. They are generated first.
                </p>
              )}
            </div>
          )
        )}
        {progress && <p className="text-sm text-content-muted" aria-live="polite">{progress}</p>}
      </div>
    </ResponsiveDialog>
  );
}
